import { type ArcWindow, arcMmToWindowFraction, windowFractionToArcMm } from './arc-window';
import type { Rect } from './types';

/**
 * 사진 위 평면 사각형(투영 영역)을 원통 area 로 되돌린다. 폭은 π/2 배로 늘고 가운데는 그대로다.
 * 위·아래는 그대로 두므로 투영 영역이 위·아래로 늘지 않은 경우(upSide ≥ 0, downSide ≤ 0)에만 enCylinderizeSize 의 역이다.
 */
export function deCylinderizeRect(projected: Rect): Rect {
  const width = (projected.width * Math.PI) / 2;
  return {
    left: projected.left + projected.width / 2 - width / 2,
    top: projected.top,
    width,
    height: projected.height,
  };
}

/** area 끝을 판정할 때 부동소수 오차로 넘친 비율을 끝값으로 보는 허용치(area 폭 대비). */
const AREA_FRACTION_EPSILON = 1e-12;

/** area 의 left 가 유한수이고 width 가 0 보다 큰 유한수인지 본다. */
function isValidAreaSpan(area: Pick<Rect, 'left' | 'width'>): boolean {
  return Number.isFinite(area.left) && Number.isFinite(area.width) && area.width > 0;
}

/**
 * 편집 모드에서 area 가로 px 을 가시 호의 원주 위치(mm)로 바꾼다. area 는 창 전체에 대응한다.
 * area 밖이거나 숫자가 아니거나 area·창이 잘못됐으면 null 이다. 뒷면 위치로 감지 않는다.
 */
export function areaPxToArcMm(
  x: number,
  area: Pick<Rect, 'left' | 'width'>,
  arcWindow: ArcWindow,
): number | null {
  if (!isValidAreaSpan(area)) return null;
  const fraction = (x - area.left) / area.width;
  if (
    !Number.isFinite(fraction) ||
    fraction < -AREA_FRACTION_EPSILON ||
    fraction > 1 + AREA_FRACTION_EPSILON
  ) {
    return null;
  }
  return windowFractionToArcMm(Math.min(1, Math.max(0, fraction)), arcWindow);
}

/** 원주 위치(mm)를 area 가로 px 로 바꾼다. 창 밖(뒷면)이거나 area·창이 잘못됐거나 px 계산이 넘치면 null 이다. */
export function arcMmToAreaPx(
  arcMm: number,
  area: Pick<Rect, 'left' | 'width'>,
  arcWindow: ArcWindow,
): number | null {
  if (!isValidAreaSpan(area)) return null;
  const fraction = arcMmToWindowFraction(arcMm, arcWindow);
  if (fraction === null) return null;
  const x = area.left + fraction * area.width;
  return Number.isFinite(x) ? x : null;
}
